import {ioJsonDeleteItemAction, ioJsonFetchAction} from 'Core/store/actions/io';
import {hideLoading, hideLoadingFunction, showLoading} from 'Core/helpers/loading';
import {getArray} from 'Core/helpers/data';
import {get, omit} from 'lodash';
import {actionCreators} from "Core/store/reducers";
import * as listItemDataMap from "./dataMap/listItem";
import * as itemFilterDataMap from "./dataMap/filter";
import {reducerStoreKey} from "./reducer";
import {isSuccessful} from "Core/helpers/io";

/**
 * Reset patient record visits list Redux state to initial state
 * @return {(function(*))|*}
 */
export const resetPatientRecordVisitsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].reset());
};

/**
 * Fetch patient record visits list
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} patientId - ID of the patient.
 * @param {?string} [therapyId=null] - ID of the therapy.
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const fetchPatientRecordVisitsListAction = (
	abortCallback, patientId, therapyId = null, filter = null, pageNo = 1, perPage, sortBy, sortDir
) => dispatch => {
	return ioJsonFetchAction(
		abortCallback,
		'defaultAuthorizedApi',
		'member/visit/page-search',
		'',
		{...filter, patientId, therapyId},
		null,
		pageNo,
		perPage,
		sortBy ? sortBy : null,
		sortDir
	)(dispatch)
		// Get mapped data from response data
		.then(responseData => {
			if (isSuccessful(responseData)) {
				return ({
					...responseData,
					filter: omit(itemFilterDataMap.input(get(responseData, 'filter')), ['patientId', 'therapyId']),
					data: getArray(responseData, 'data').map(i => listItemDataMap.input(i))
				});
			}
			return undefined;
		});
};

/**
 * Load patient record visits list into Redux store
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string} patientId - ID of the patient.
 * @param {?string} [therapyId=null] - ID of the therapy. 
 * @param {Object} [filter] - Fetch filter.
 * @param {number} [pageNo] - Number of the page to load (pagination). Starts from 1.
 * @param {number} [perPage] - Number of items per page to load (pagination). Used system default if not specified.
 * @param {string} [sortBy] - Sort field name. Sort fields are defined by the API.
 * @param {SortOrder} [sortDir] - Sort direction.
 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay will be rendered.
 * @return {function(*=): Promise<IoJsonFetchResponseObject>}
 */
export const loadPatientRecordVisitsListAction = (
	abortCallback, patientId, therapyId = null, filter = null, pageNo = 1, perPage, sortBy, sortDir, 
	loadingOverlay = true
) => dispatch => {
	const loading = (loadingOverlay === true ? showLoading("#patient-record-visits-table") : null);
	return fetchPatientRecordVisitsListAction(
		abortCallback, patientId, therapyId, filter, pageNo, perPage, sortBy, sortDir
	)(dispatch)
		// Load data into Redux store
		.then(responseData => {
			if (responseData) dispatch(actionCreators[reducerStoreKey].setPatientRecordVisitsListData(responseData));
			if (loading !== null) hideLoading(loading);
			return responseData;
		});
};

/**
 * Set patient record visits list filter in Redux store
 *
 * @param {Object} filter - Filter to set.
 * @return {(function(*): void)|*}
 */
export const setPatientRecordVisitsFilterAction = filter => dispatch => {
	dispatch(actionCreators[reducerStoreKey].setPatientRecordVisitsListFilter(filter));
};

/**
 * Clear patient record visits list form Redux store
 *
 * @return {(function(*): void)|*}
 */
export const clearPatientRecordVisitsListAction = () => dispatch => {
	dispatch(actionCreators[reducerStoreKey].clearPatientRecordVisitsListData());
}

/**
 * Delete visits item action
 *
 * @param {function} [abortCallback=(abortController)=>{}] - Callback function that will receive AbortController as an
 * argument.
 * @param {string|number} id - ID of the visits item that will be deleted.
 * @return {function(*): Promise<IoJsonFetchResponseObject>}
 */
export const deleteVisitsItemAction = (abortCallback, id) => dispatch => {
	const loading = showLoading('#delete-visit-dialog .dialog-content-component .buttons');
	return ioJsonDeleteItemAction(
		// @note abortCallback is set to undefined because delete actions should not be cancelable.
		undefined,
		'defaultAuthorizedApi',
		'member/visit/delete-by-ids',
		id,
		{},
		hideLoadingFunction(loading)
	)(dispatch);
}
